import { z } from 'zod';
import { toModelId } from '@/shared/types/common.types';
import { guidSchema, numericEnum } from '@/shared/utils/api/serverPrimitives';
import type {
  ProjectFamily,
  ProjectFamilyInstance,
  ProjectSheet,
  ProjectSummary,
} from '../types/project.types';

/**
 * The wire contract for `RevitFileProjectController`.
 *
 * These responses are protobuf messages handed to the JSON serialiser rather than
 * hand-written DTOs, which shows in two ways worth knowing: the payload is wrapped in a
 * message (`{ projects: [...] }`, `{ project: {...} }`) rather than being a bare array or
 * object, and protobuf's defaults mean a field the mapper never sets still appears — as
 * `0`, `""` or `[]`. Everything is therefore optional here and defaulted, because an
 * absent field and an unset one are indistinguishable on this endpoint.
 */

const relationType = numericEnum('RevitDocumentRelationType', {
  0: 'Regular',
  1: 'Central',
  2: 'Local',
  3: 'Detached',
} as const);

/** `RevitFamilyInstanceModel`, as nested in a project detail response. */
export const familyInstanceModelSchema = z.object({
  id: z.number().int().default(0),
  name: z.string().default(''),
  uniqueId: z.string().default(''),
  parentInstanceUniqueId: z.string().default(''),
  // `int64` in protobuf, which the JSON serialiser may write as a string.
  elementId: z.union([z.number(), z.string()]).default(0),
  // Declared on the message, but the mapper assigns `familyInstance.Name` to it, so it only
  // ever repeats `name`. Parsed to keep the contract complete and deliberately not mapped.
  familyName: z.string().default(''),
  // `FamilyType?.Name` — the one field here that says something `name` does not.
  familyType: z.string().default(''),
  // Empty when the instance has no family type.
  familyTypeUniqueId: z.string().default(''),
  category: z.object({ name: z.string().default('') }).default({ name: '' }),
  parameters: z.array(z.object({
    name: z.string().default(''),
    value: z.string().default(''),
    type: z.string().default(''),
    isInstance: z.boolean().default(false),
  })).default([]),
  // Additive Core field; legacy servers omit it and cannot prove the project-family link.
  projectFamilyId: z.number().int().nonnegative().default(0),
});

/** `RevitFileProjectModel`. */
export const projectModelSchema = z.object({
  id: z.number().int(),
  name: z.string().default(''),
  path: z.string().default(''),
  title: z.string().default(''),
  hashId: guidSchema.default(''),
  // `.catch` rather than `.default`: protobuf sends 0 for an unset enum, which decodes to
  // `Regular`, but an unrecognised value should also read as a standalone file rather than
  // fail a whole project list over one row.
  revitDocumentRelationType: relationType.schema.catch('Regular'),
  schedulesIds: z.array(z.number().int()).default([]),
  sheetsIds: z.array(z.number().int()).default([]),
  viewsIds: z.array(z.number().int()).default([]),
  revitFamilyInstanceIds: z.array(z.number().int()).default([]),
  revitProjectFamilyIds: z.array(z.number().int()).default([]),
  familyInstances: z.array(familyInstanceModelSchema).default([]),
});

/** `GetAllRevitFileProjectsResponse`. */
export const projectListResponseSchema = z.object({
  projects: z.array(projectModelSchema).default([]),
});

/** `RevitFileProjectResponse`. */
export const projectResponseSchema = z.object({
  project: projectModelSchema,
});

export type ProjectModelWire = z.infer<typeof projectModelSchema>;

/**
 * Synchronisation facts about a model, gathered from its snapshots.
 *
 * The project endpoint's mapper does not expose `ModelVersion`, `LastSynchronized` or any
 * author, even though `RevitFIleDocument` carries all three. They are read from
 * `SnapshotMetaData` instead, which is the record that produced them.
 */
export interface ModelSyncFacts {
  readonly modelVersion: number | null;
  readonly lastSynchronized: string | null;
  readonly snapshotCount: number;
  readonly fileSizeBytes: number | null;
  readonly authorFullName: string | null;
}

export const NO_SYNC_FACTS: ModelSyncFacts = {
  modelVersion: null,
  lastSynchronized: null,
  snapshotCount: 0,
  fileSizeBytes: null,
  authorFullName: null,
};

export function toProjectSummary(
  raw: ProjectModelWire,
  sync: ModelSyncFacts = NO_SYNC_FACTS
): ProjectSummary {
  return {
    id: toModelId(raw.id),
    name: raw.name,
    // Protobuf sends "" for a title the mapper left unset; the name is the better fallback.
    title: raw.title.length > 0 ? raw.title : raw.name,
    path: raw.path,
    relationType: raw.revitDocumentRelationType,
    hashId: raw.hashId,
    sheetCount: raw.sheetsIds.length,
    viewCount: raw.viewsIds.length,
    scheduleCount: raw.schedulesIds.length,
    familyInstanceCount: raw.revitFamilyInstanceIds.length,
    projectFamilyCount: raw.revitProjectFamilyIds.length,
    ...sync,
  };
}

export function toFamilyInstance(
  raw: z.infer<typeof familyInstanceModelSchema>
): ProjectFamilyInstance {
  return {
    id: raw.id,
    name: raw.name,
    uniqueId: raw.uniqueId,
    parentInstanceUniqueId: raw.parentInstanceUniqueId,
    elementId: Number(raw.elementId),
    familyType: raw.familyType,
    familyTypeUniqueId: raw.familyTypeUniqueId,
    categoryName: raw.category.name,
    parameters: raw.parameters.map((parameter) => ({
      name: parameter.name,
      value: parameter.value,
      type: parameter.type,
      isInstance: parameter.isInstance,
    })),
    projectFamilyId: raw.projectFamilyId === 0 ? null : raw.projectFamilyId,
  };
}

/**
 * `RevitProjectFamilyModel` as `GetProjectFamiliesResponse.families[]`.
 *
 * Lighter than a library family: enough to list what is loaded in the model.
 */
export const projectFamilyModelSchema = z.object({
  id: z.number().int().default(0),
  name: z.string().default(''),
  uniqueId: z.string().default(''),
  elementId: z.union([z.number(), z.string()]).default(0),
  category: z
    .object({
      name: z.string().default(''),
      id: z.number().int().default(-1),
    })
    .default({ name: '', id: -1 }),
  familyTypes: z.array(z.unknown()).default([]),
  parameters: z.array(z.unknown()).default([]),
});

export const projectFamiliesResponseSchema = z.object({
  families: z.array(projectFamilyModelSchema).default([]),
});

/** `CountResponse` from `RevitProjectFamily/GetById?isCount=true`. */
export const projectFamilyCountSchema = z.object({
  count: z.number().int().nonnegative(),
});

const projectSheetParameterSchema = z.object({
  name: z.string().default(''),
  value: z.string().default(''),
  type: z.string().default(''),
});

/** One row of `RevitProjectContentSearch/Query` when `elementTypes` is `Sheet`. */
export const projectSheetResultSchema = z.object({
  id: z.number().int(),
  name: z.string().default(''),
  elementId: z.union([z.number(), z.string()]).default(0),
  uniqueId: z.string().default(''),
  category: z.string().default(''),
  parameters: z.array(projectSheetParameterSchema).default([]),
});

export const projectSheetSearchResponseSchema = z.object({
  totalCount: z.number().int().nonnegative().default(0),
  results: z.array(projectSheetResultSchema).default([]),
});

export function toProjectFamily(
  raw: z.infer<typeof projectFamilyModelSchema>
): ProjectFamily {
  return {
    id: raw.id,
    name: raw.name,
    uniqueId: raw.uniqueId,
    elementId: Number(raw.elementId),
    categoryName: raw.category.name,
    typeCount: raw.familyTypes.length,
    parameterCount: raw.parameters.length,
  };
}

export function toProjectSheet(
  raw: z.infer<typeof projectSheetResultSchema>
): ProjectSheet {
  return {
    id: raw.id,
    name: raw.name,
    uniqueId: raw.uniqueId,
    elementId: Number(raw.elementId),
    categoryName: raw.category,
    parameters: raw.parameters.map((parameter) => ({
      name: parameter.name,
      value: parameter.value,
      type: parameter.type,
    })),
  };
}

